import { useMemo, useState } from 'react';
import { useEffect } from 'react';
import { FormEvent, useCallback, useRef } from 'react';

import { FaFilterCircleXmark } from 'react-icons/fa6';
import { FiSearch } from 'react-icons/fi';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { MdFilterList } from 'react-icons/md';
import ReactPaginate from 'react-paginate';
import { MoonLoader } from 'react-spinners';

import { IconPieChartSvg } from '~/assets/icons';
import { InputRange, Layout } from '~/components';
import { useAuth } from '~/hooks/auth';
import api from '~/services/api';

import { Card } from './Card';
import Modal from './Modal';
import { TrainingProgram } from './types';
import { getProfileCode } from './utils';

import {
  Container,
  Header,
  ContentRight,
  InputContainer,
  ButtonExportReport,
  ModalFilterContent,
  ModalTitle,
  CardContainer,
  ModalLineContainer,
  FieldSet,
  ModalButton,
  FieldContainer,
  FieldSetLabel,
  CheckboxSet,
  CheckButton,
  AdvancedSettingsButton,
  AdvancedSettingsContainer,
  AdvancedSettingsSet,
  ProfileCodeLabel,
  Divider,
  AlertContainer,
  NoDataFound,
  PaginateContainer,
} from './styles';

const Manager = () => {
  const searchRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [profileCodeHash, setProfileCodeHash] = useState('');
  const [modalFilterIsOpen, setModalFilterIsOpen] = useState(false);
  const [deadline, setDeadline] = useState<number>(0);
  const [advancedSettings, setAdvancedSettings] = useState(false);
  const [trainingPrograms, setTrainingPrograms] = useState<TrainingProgram[]>(
    [],
  );
  const [selectedBillingStatusBox, setSelectedBillingStatusBox] = useState('');
  const [selectedTrainingStatusBox, setSelectedTrainingStatusBox] =
    useState('');
  const [selectedRegisterStatus, setSelectedRegisterStatus] = useState('');
  const [selectedGenderBox, setSelectedGenderBox] = useState('');
  const [selectedStudentLevelBox, setSelectedStudentLevelBox] = useState('');
  const [selectedPhysicalEvaluationBox, setSelectedPhysicalEvaluationBox] =
    useState('');
  const [selectedAvailableTimeBox, setSelectedAvailableTimeBox] = useState('');
  const [selectedObjectiveBox, setSelectedObjectiveBox] = useState('');
  const [selectedFrequencyBox, setSelectedFrequencyBox] = useState('');

  // get data to fill cards
  const getTrainingPrograms = useCallback(async () => {
    setLoading((prev) => !prev);

    const { data: apiData } = await api.get(`training-programs`);

    setTrainingPrograms(apiData.data);
    setLastPage(apiData.lastPage);
    setLoading((prev) => !prev);
  }, []);

  useEffect(() => {
    getTrainingPrograms();
  }, [getTrainingPrograms]);

  // logic to show profile code hash
  useEffect(() => {
    const profileCode = getProfileCode({
      studentLevelLabel: selectedStudentLevelBox,
      frequencyLabel: selectedFrequencyBox,
      genderLabel: selectedGenderBox,
      availableTimeLabel: selectedAvailableTimeBox,
      physicalEvaluationLabel: selectedPhysicalEvaluationBox,
      objectiveLabel: selectedObjectiveBox,
    });

    setProfileCodeHash(profileCode);
  }, [
    selectedStudentLevelBox,
    selectedFrequencyBox,
    selectedGenderBox,
    selectedAvailableTimeBox,
    selectedPhysicalEvaluationBox,
    selectedObjectiveBox,
  ]);

  const checkBoxTrainingStatusOptions = useMemo(() => {
    return [
      { value: 'pending', label: 'pendente' },
      { value: 'emited', label: 'emitido' },
      { value: 'canceled', label: 'cancelado' },
    ];
  }, []);

  const CheckBoxTrainingStatus = useMemo(
    () =>
      checkBoxTrainingStatusOptions.map((item) => (
        <CheckButton
          key={`training_status_${item.value}`}
          isSelected={selectedTrainingStatusBox === item.value}
          onClick={() =>
            selectedTrainingStatusBox === item.value
              ? setSelectedTrainingStatusBox('')
              : setSelectedTrainingStatusBox(item.value)
          }
        >
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </CheckButton>
      )),
    [checkBoxTrainingStatusOptions, selectedTrainingStatusBox],
  );

  const checkBoxGenderOptions = useMemo(() => {
    return [
      { value: 'NA', label: 'SEM INFO' },
      { value: 'male', label: 'MASCULINO' },
      { value: 'female', label: 'FEMININO' },
      { value: 'other', label: 'OUTROS' },
    ];
  }, []);

  const CheckBoxGender = useMemo(
    () =>
      checkBoxGenderOptions.map((item) => (
        <CheckButton
          key={`gender_${item.value}`}
          isSelected={selectedGenderBox === item.value}
          onClick={() =>
            selectedGenderBox === item.value
              ? setSelectedGenderBox('')
              : setSelectedGenderBox(item.value)
          }
        >
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </CheckButton>
      )),
    [checkBoxGenderOptions, selectedGenderBox],
  );

  const checkBoxStudentLevelOptions = useMemo(() => {
    return [
      { value: 'NT00', label: 'SEM INFO' },
      { value: 'NT01', label: 'SEDENTÁRIO' },
      { value: 'NT02', label: 'INICIANTE' },
      { value: 'NT03', label: 'INTERMEDIÁRIO' },
      { value: 'NT04', label: 'AVANÇADO' },
      { value: 'NT05', label: 'ATLETA' },
    ];
  }, []);

  const CheckBoxStudentLevel = useMemo(
    () =>
      checkBoxStudentLevelOptions.map((item) => (
        <CheckButton
          key={`student_level_${item.value}`}
          isSelected={selectedStudentLevelBox === item.value}
          onClick={() =>
            selectedStudentLevelBox === item.value
              ? setSelectedStudentLevelBox('')
              : setSelectedStudentLevelBox(item.value)
          }
        >
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </CheckButton>
      )),
    [checkBoxStudentLevelOptions, selectedStudentLevelBox],
  );

  const checkBoxPhysicalEvaluationOptions = useMemo(() => {
    return [
      { value: 'SEM INFO', label: 'SEM INFO' },
      { value: 'BAIXO PESO', label: 'BAIXO PESO' },
      { value: 'PESO NORMAL', label: 'PESO NORMAL' },
      { value: 'EXCESSO DE PESO', label: 'EXCESSO DE PESO' },
      { value: 'OBESIDADE 01', label: 'OBESIDADE 01' },
      { value: 'OBESIDADE 02', label: 'OBESIDADE 02' },
      { value: 'OBESIDADE 03', label: 'OBESIDADE 03' },
    ];
  }, []);

  const CheckBoxPhysicalEvaluation = useMemo(
    () =>
      checkBoxPhysicalEvaluationOptions.map((item) => (
        <CheckButton
          key={`physical_evaluation_${item.value}`}
          isSelected={selectedPhysicalEvaluationBox === item.value}
          onClick={() =>
            selectedPhysicalEvaluationBox === item.value
              ? setSelectedPhysicalEvaluationBox('')
              : setSelectedPhysicalEvaluationBox(item.value)
          }
        >
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </CheckButton>
      )),
    [checkBoxPhysicalEvaluationOptions, selectedPhysicalEvaluationBox],
  );

  const checkBoxObjectiveOptions = useMemo(() => {
    return [
      { value: 'OT00', label: 'SEM INFO' },
      { value: 'OT01', label: 'CONDICIONAMENTO' },
      { value: 'OT02', label: 'EMAGRECIMENTO' },
      { value: 'OT03', label: 'ESTÉTICA' },
      { value: 'OT04', label: 'HIPERTROFIA' },
      { value: 'OT05', label: 'QUALIDADE DE VIDA' },
    ];
  }, []);

  const CheckBoxObjective = useMemo(
    () =>
      checkBoxObjectiveOptions.map((item) => (
        <CheckButton
          key={`objective_${item.value}`}
          isSelected={selectedObjectiveBox === item.value}
          onClick={() =>
            selectedObjectiveBox === item.value
              ? setSelectedObjectiveBox('')
              : setSelectedObjectiveBox(item.value)
          }
        >
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </CheckButton>
      )),
    [checkBoxObjectiveOptions, selectedObjectiveBox],
  );

  const checkBoxFrequencyOptions = useMemo(() => {
    return [
      { value: 'FQ00', label: 'SEM INFO' },
      { value: 'FQ01', label: '1X' },
      { value: 'FQ02', label: '2X' },
      { value: 'FQ03', label: '3X' },
      { value: 'FQ04', label: '4X' },
      { value: 'FQ05', label: '5X' },
      { value: 'FQ06', label: '6X' },
      { value: 'FQ07', label: '7X' },
    ];
  }, []);

  const CheckBoxFrequency = useMemo(
    () =>
      checkBoxFrequencyOptions.map((item) => (
        <CheckButton
          key={`frequency_${item.value}`}
          isSelected={selectedFrequencyBox === item.value}
          onClick={() =>
            selectedFrequencyBox === item.value
              ? setSelectedFrequencyBox('')
              : setSelectedFrequencyBox(item.value)
          }
        >
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </CheckButton>
      )),
    [checkBoxFrequencyOptions, selectedFrequencyBox],
  );

  const checkBoxAvailableTimeOptions = useMemo(() => {
    return [
      { value: 'TA00', label: 'SEM INFO' },
      { value: 'TA01', label: '30 MINUTOS' },
      { value: 'TA02', label: '30-45 MINUTOS' },
      { value: 'TA03', label: '45-60 MINUTOS' },
      { value: 'TA04', label: '60-90 MINUTOS' },
      { value: 'TA05', label: '90 +' },
    ];
  }, []);

  const CheckBoxAvailableTime = useMemo(
    () =>
      checkBoxAvailableTimeOptions.map((item) => (
        <CheckButton
          key={`available_time_${item.value}`}
          isSelected={selectedAvailableTimeBox === item.value}
          onClick={() =>
            selectedAvailableTimeBox === item.value
              ? setSelectedAvailableTimeBox('')
              : setSelectedAvailableTimeBox(item.value)
          }
        >
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </CheckButton>
      )),
    [checkBoxAvailableTimeOptions, selectedAvailableTimeBox],
  );

  const CardsMemo = useMemo(
    () =>
      trainingPrograms.length > 0 ? (
        <CardContainer>
          {trainingPrograms.map((program) => (
            <Card key={program.id} data={program} />
          ))}
        </CardContainer>
      ) : (
        <NoDataFound>
          <h1>Nenhum dado encontrado!</h1>
        </NoDataFound>
      ),
    [trainingPrograms],
  );

  const handleFilters = useCallback(async () => {
    setLoading((prev) => !prev);

    const { data: apiData } = await api.get('training-programs', {
      params: {
        status_billing: selectedBillingStatusBox,
        status: selectedRegisterStatus,
        status_training: selectedTrainingStatusBox,
        profile_code: profileCodeHash,
        deadline,
        perpage: 100,
        page: 1,
      },
    });

    setTrainingPrograms(apiData.data);
    setLastPage(apiData.lastPage);

    setLoading((prevState) => !prevState);
    setModalFilterIsOpen((prev) => !prev);
  }, [
    profileCodeHash,
    selectedBillingStatusBox,
    selectedRegisterStatus,
    selectedTrainingStatusBox,
    deadline,
  ]);

  const handleSearch = useCallback(async (e: FormEvent) => {
    setLoading((prev) => !prev);

    e.preventDefault();

    const { data: apiData } = await api.get('training-programs', {
      params: {
        name: searchRef.current?.value,
      },
    });

    setTrainingPrograms(apiData.data);

    setLoading((prev) => !prev);
  }, []);

  const handlePageClick = useCallback(async (data) => {
    setLoading((prevState) => !prevState);

    const pageSelected = data.selected + 1;

    const { data: apiData } = await api.get('training-programs', {
      params: {
        page: pageSelected,
      },
    });

    setTrainingPrograms(apiData.data);
    setLastPage(apiData.lastPage);
    setLoading((prevState) => !prevState);
  }, []);

  const handleResetFilter = () => {
    setDeadline(0);
    setSelectedTrainingStatusBox('');
    setSelectedGenderBox('');
    setSelectedStudentLevelBox('');
    setSelectedPhysicalEvaluationBox('');
    setSelectedObjectiveBox('');
    setSelectedFrequencyBox('');
    setSelectedAvailableTimeBox('');
  };

  const ModalFilter = useCallback(() => {
    return (
      <Modal
        isOpen={modalFilterIsOpen}
        setOpen={setModalFilterIsOpen}
        maxWidthStyled={734}
        maxHeightStyled={736}
      >
        <ModalFilterContent>
          <ModalTitle>
            Filtrar
            <FaFilterCircleXmark size={25} onClick={handleResetFilter} />
          </ModalTitle>

          <ModalLineContainer>
            <FieldSet>
              <InputRange
                min={-6}
                max={11}
                steps={1}
                data={deadline}
                setData={setDeadline}
              />
            </FieldSet>
          </ModalLineContainer>

          <ModalLineContainer>
            <FieldContainer>
              <FieldSet />
              <FieldSet>
                <FieldSetLabel>Status de treino:</FieldSetLabel>

                <CheckboxSet>{CheckBoxTrainingStatus}</CheckboxSet>
              </FieldSet>
              <FieldSet />
            </FieldContainer>
          </ModalLineContainer>

          <ModalLineContainer>
            <FieldContainer>
              <AdvancedSettingsSet>
                <AdvancedSettingsButton
                  onClick={() => setAdvancedSettings(!advancedSettings)}
                >
                  <p>Opções avançadas &nbsp;</p>
                  {advancedSettings ? (
                    <IoIosArrowDown size={22} />
                  ) : (
                    <IoIosArrowUp size={22} />
                  )}
                </AdvancedSettingsButton>

                <AdvancedSettingsContainer isActive={advancedSettings}>
                  <ProfileCodeLabel>
                    ProfileCode: <br />
                    {profileCodeHash}
                  </ProfileCodeLabel>

                  <FieldSet>
                    <FieldSetLabel>Sexo:</FieldSetLabel>

                    <CheckboxSet>{CheckBoxGender}</CheckboxSet>
                  </FieldSet>

                  <Divider />

                  <FieldSet>
                    <FieldSetLabel>Nível técnico:</FieldSetLabel>

                    <CheckboxSet>{CheckBoxStudentLevel}</CheckboxSet>
                  </FieldSet>

                  <Divider />

                  <FieldSet>
                    <FieldSetLabel>Avaliação física:</FieldSetLabel>

                    <CheckboxSet>{CheckBoxPhysicalEvaluation}</CheckboxSet>
                  </FieldSet>

                  <Divider />

                  <FieldSet>
                    <FieldSetLabel>Objetivo:</FieldSetLabel>

                    <CheckboxSet>{CheckBoxObjective}</CheckboxSet>
                  </FieldSet>

                  <Divider />

                  <FieldSet>
                    <FieldSetLabel>Frequência:</FieldSetLabel>

                    <CheckboxSet>{CheckBoxFrequency}</CheckboxSet>
                  </FieldSet>

                  <Divider />

                  <FieldSet>
                    <FieldSetLabel>Tempo disponível:</FieldSetLabel>

                    <CheckboxSet>{CheckBoxAvailableTime}</CheckboxSet>
                  </FieldSet>
                </AdvancedSettingsContainer>
              </AdvancedSettingsSet>
            </FieldContainer>
          </ModalLineContainer>

          <ModalButton onClick={handleFilters}>Aplicar</ModalButton>
        </ModalFilterContent>
      </Modal>
    );
  }, [
    modalFilterIsOpen,
    deadline,
    CheckBoxTrainingStatus,
    advancedSettings,
    profileCodeHash,
    CheckBoxGender,
    CheckBoxStudentLevel,
    CheckBoxPhysicalEvaluation,
    CheckBoxObjective,
    CheckBoxFrequency,
    CheckBoxAvailableTime,
    handleFilters,
  ]);

  return (
    <Layout title="I.MANAGER">
      <Container>
        <Header>
          {ModalFilter()}

          <ContentRight>
            <InputContainer>
              <form onSubmit={handleSearch}>
                <FiSearch size={30} color="#000" />

                <input
                  type="text"
                  placeholder="Buscar por aluno"
                  name="search"
                  id="search"
                  ref={searchRef}
                />

                <MdFilterList
                  size={30}
                  color="#000"
                  onClick={() => setModalFilterIsOpen(true)}
                />
              </form>
            </InputContainer>

            <ButtonExportReport
              href={`${process.env.REACT_APP_API_URL}/reports/${user.professional.id}/crm-imanager`}
              target="blank"
            >
              <IconPieChartSvg />

              <div>
                <p>Relatório comercial</p>
              </div>
            </ButtonExportReport>
          </ContentRight>
        </Header>

        {loading ? (
          <AlertContainer>
            <MoonLoader size={30} color="#FFDF22" />
          </AlertContainer>
        ) : (
          CardsMemo
        )}

        <PaginateContainer>
          <ReactPaginate
            nextLabel="Próxima"
            pageCount={lastPage}
            marginPagesDisplayed={0}
            pageRangeDisplayed={10}
            onPageChange={handlePageClick}
            containerClassName="pagination"
            activeClassName="active"
          />
        </PaginateContainer>
      </Container>
    </Layout>
  );
};

export default Manager;
